/**
 * CartSummaryV2 Styled Components
 *
 * Visual styling aligned with Shapermint's CartSummaryV2 pattern:
 * - Subtotal row with compare-at strikethrough + bold total
 * - Savings row in brand green (#2f806a / semantic.positive)
 * - Full-width checkout CTA with lock icon styling
 * - Free shipping progress bar with animated fill
 */

import styled from '@emotion/styled';
import { ButtonPrimary, Text } from '@template-project/afrodita-components';

/* ─── Summary Layout ─────────────────────────────────────────── */

export const SummaryContainer = styled.div`
  width: 100%;
`;

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 24px;
`;

export const RowPrices = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-left: auto;
`;

/* ─── Subtotal ───────────────────────────────────────────────── */

export const SubtotalLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 22px !important;
`;

export const CompareAtPrice = styled(Text)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 20px !important;
  text-decoration-line: line-through;
`;

export const TotalPrice = styled(Text)`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px !important;
  font-weight: 700 !important;
  line-height: 20px !important;
`;

/* ─── Savings ────────────────────────────────────────────────── */

export const SavingsRow = styled(Row)`
  margin-bottom: 10px;
`;

export const SavingsLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.semantic.positive.color};
  font-size: 13px !important;
  font-weight: 700 !important;
  line-height: 24px !important;
  text-transform: uppercase;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

export const SavingsValue = styled(Text)`
  color: ${({ theme }) => theme.colors.semantic.positive.color};
  text-align: right;
  font-size: 14px !important;
  font-weight: 700 !important;
  line-height: 24px !important;
`;

/* ─── Checkout CTA ───────────────────────────────────────────── */

export const CheckoutButton = styled(ButtonPrimary)`
  padding: 12px 16px;
  font-size: 16px !important;
  font-weight: 600 !important;
  line-height: 20px !important;
  text-transform: uppercase;
`;

export const CheckoutButtonContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

export const ErrorMessage = styled(Text)`
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  margin-bottom: 12px;
  text-align: center;
`;

/* ─── Free Shipping Progress Bar ─────────────────────────────── */

export const ProgressBarContainer = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

export const ProgressBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.border.color};
  border-radius: 4px;
  overflow: hidden;
`;

export const ProgressBarFill = styled.div<{ $percent: number; $unlocked?: boolean }>`
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  background: ${({ theme, $unlocked }) =>
    $unlocked
      ? theme.colors.semantic.positive.color
      : theme.colors.pallete.primary.color};
  border-radius: 4px;
  transition: width 0.4s ease, background-color 0.3s ease;
`;

export const ProgressBarLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
`;

export const ProgressBarLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ProgressBarMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin-top: 8px;
  line-height: 20px;

  strong {
    font-weight: 700;
  }
`;

export const ProgressBarMessageBold = styled.span`
  font-weight: 700;
`;

/* ─── Bookmark / Savings Icon ────────────────────────────────── */

export const SavingsIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.semantic.positive.color};

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;
